// ════════════════════════════════════════════════════════════════════════════
//  Udisys Gestor Web  —  pages/Relatorios/RelatoriosVendas.tsx
//  Relatórios de Vendas: Cliente, Produto, Forma de Pagamento, Evolução.
// ════════════════════════════════════════════════════════════════════════════

import { useState, useMemo } from 'react';
import { Users, Package, CreditCard, TrendingUp, ChevronLeft, ChevronRight } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Progress } from '@/components/ui/progress';
import { Button } from '@/components/ui/button';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import {
  useVendasClientesSintetico,
  useVendasClientesAnalitico,
  useVendasProdutosSintetico,
  useVendasProdutosAnalitico,
  useHistoricoCliente,
  useVendasFormasPagamento,
  useEvolucaoVendas,
} from '@/features/relatorios/hooks/useRelatorios';
import { BotaoExportar } from '@/components/BotaoExportar';
import { SortableTableHead } from '@/components/SortableTableHead';
import { alternarOrdenacao, type DirecaoOrdenacao } from '@/lib/consultas';
import {
  FiltroPeriodo, FiltroPessoa, FiltroProduto, FiltroGrupoProduto, hoje, diasAtras, anoInicio,
} from './FiltrosRelatorio';

// ── Helpers ───────────────────────────────────────────────────────────────────

const fmtCur = (v: number) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v);

const fmtNum = (v: number, dec = 2) =>
  new Intl.NumberFormat('pt-BR', { minimumFractionDigits: dec, maximumFractionDigits: dec }).format(v);

const PAGE_SIZES = [10, 20, 50] as const;

/**
 * Ordenação no servidor (whitelist em RelatoriosVendasController). Campo novo entra
 * crescente se for texto/código e decrescente se for valor/data; trocar volta à pág. 1.
 */
function useOrdenacaoServidor<T extends string>(inicial: T, camposTexto: readonly T[], onMudar: () => void) {
  const [ordem, setOrdem] = useState<{ ordenarPor: T; direcao: DirecaoOrdenacao }>({ ordenarPor: inicial, direcao: 'desc' });
  const onOrdenar = (campo: T) => {
    setOrdem((atual) => alternarOrdenacao(atual, campo, camposTexto.includes(campo) ? 'asc' : 'desc'));
    onMudar();
  };
  return { ...ordem, cabecalho: { ordenarPor: ordem.ordenarPor, direcao: ordem.direcao, onOrdenar } };
}

function PaginacaoBar({
  pagina, setPagina, tamanhoPagina, setTamanhoPagina, total, totalPaginas,
}: {
  pagina: number; setPagina: (p: number) => void;
  tamanhoPagina: number; setTamanhoPagina: (t: number) => void;
  total: number; totalPaginas: number;
}) {
  const first = (pagina - 1) * tamanhoPagina + 1;
  const last  = Math.min(pagina * tamanhoPagina, total);
  return (
    <div className="flex flex-wrap items-center justify-between gap-4 pt-2 border-t text-sm text-muted-foreground">
      <span>{total === 0 ? 'Nenhum registro' : `${first}–${last} de ${total}`}</span>
      <div className="flex items-center gap-3">
        <div className="flex items-center gap-2">
          <span className="whitespace-nowrap">Por página:</span>
          <Select value={String(tamanhoPagina)} onValueChange={(v) => { setTamanhoPagina(Number(v)); setPagina(1); }}>
            <SelectTrigger className="w-[70px] h-8"><SelectValue /></SelectTrigger>
            <SelectContent>{PAGE_SIZES.map((s) => <SelectItem key={s} value={String(s)}>{s}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="flex items-center gap-1">
          <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => setPagina(pagina - 1)} disabled={pagina <= 1}>
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <span className="px-2">Pág {pagina} / {totalPaginas}</span>
          <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => setPagina(pagina + 1)} disabled={pagina >= totalPaginas}>
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}

// ── Visão Sintético/Analítico e drill-down ────────────────────────────────────

type Visao = 'sintetico' | 'analitico';

/** Sintético = uma linha por cliente/produto; Analítico = um DAV (ou item de DAV) por linha. */
function SeletorVisao({ value, onChange }: { value: Visao; onChange: (v: Visao) => void }) {
  return (
    <div className="space-y-1">
      <span className="text-xs text-muted-foreground">Visão</span>
      <ToggleGroup
        type="single" variant="outline" size="sm" value={value}
        onValueChange={(v) => { if (v) onChange(v as Visao); }}
      >
        <ToggleGroupItem value="sintetico">Sintético</ToggleGroupItem>
        <ToggleGroupItem value="analitico">Analítico</ToggleGroupItem>
      </ToggleGroup>
    </div>
  );
}

const fmtData = (iso: string) => new Date(iso).toLocaleDateString('pt-BR');

/** Cliente aberto no drill-down. "Consumidor Final" (sem cadastro) não tem histórico. */
interface ClienteDrill { id: number; nome: string }

function NomeClienteLink({ id, nome, onAbrir }: { id: number | null; nome: string; onAbrir: (c: ClienteDrill) => void }) {
  if (!id) return <>{nome}</>;
  return (
    <button
      type="button"
      className="text-left text-primary hover:underline"
      title="Ver os documentos do cliente no período"
      onClick={() => onAbrir({ id, nome })}
    >
      {nome}
    </button>
  );
}

/** Documentos (DAVs) do cliente no período filtrado na aba. */
function HistoricoClienteDialog({
  cliente, dtInicio, dtFim, onClose,
}: { cliente: ClienteDrill | null; dtInicio: string; dtFim: string; onClose: () => void }) {
  const [pagina, setPagina] = useState(1);
  const [tam,    setTam]    = useState(10);

  const { data, isLoading } = useHistoricoCliente({
    clienteId: cliente?.id ?? 0, dtInicio, dtFim, pagina, tamanhoPagina: tam,
  });

  return (
    <Dialog open={!!cliente} onOpenChange={(o) => { if (!o) { onClose(); setPagina(1); } }}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>{cliente?.nome}</DialogTitle>
          <DialogDescription>
            Documentos de {fmtData(`${dtInicio}T00:00:00`)} a {fmtData(`${dtFim}T00:00:00`)}
          </DialogDescription>
        </DialogHeader>
        <div className="overflow-auto max-h-[60vh]">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="text-right">DAV</TableHead>
                <TableHead>Emissão</TableHead>
                <TableHead>Forma de Pagamento</TableHead>
                <TableHead className="text-right">Itens</TableHead>
                <TableHead className="text-right">Total Bruto</TableHead>
                <TableHead className="text-right">Desconto</TableHead>
                <TableHead className="text-right">Total Líquido</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow><TableCell colSpan={7}><Skeleton className="h-8 w-full" /></TableCell></TableRow>
              ) : !data?.itens.length ? (
                <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-8">Nenhum documento no período</TableCell></TableRow>
              ) : data.itens.map((r) => (
                <TableRow key={r.numeroDav}>
                  <TableCell className="text-right tabular-nums font-medium">{r.numeroDav}</TableCell>
                  <TableCell className="text-muted-foreground text-sm">{fmtData(r.dtEmissao)}</TableCell>
                  <TableCell>{r.formaPagamento ?? '—'}</TableCell>
                  <TableCell className="text-right tabular-nums">{r.qtdItens}</TableCell>
                  <TableCell className="text-right tabular-nums">{fmtCur(r.totalBruto)}</TableCell>
                  <TableCell className="text-right tabular-nums text-red-500">{r.totalDesconto > 0 ? `-${fmtCur(r.totalDesconto)}` : '—'}</TableCell>
                  <TableCell className="text-right tabular-nums font-semibold">{fmtCur(r.totalLiquido)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
        {data && <PaginacaoBar pagina={pagina} setPagina={setPagina} tamanhoPagina={tam} setTamanhoPagina={setTam} total={data.total} totalPaginas={data.totalPaginas} />}
      </DialogContent>
    </Dialog>
  );
}

// ── Aba Vendas por Cliente ────────────────────────────────────────────────────

type CampoCliente = 'codigo' | 'nome' | 'qtd' | 'bruto' | 'desconto' | 'liquido' | 'ultimaVenda';
type CampoProduto = 'codigo' | 'descricao' | 'qtd' | 'total' | 'desconto' | 'liquido';

function TabClientes() {
  const [dtInicio, setDtInicio] = useState(diasAtras(30));
  const [dtFim,    setDtFim]    = useState(hoje());
  const [pagina,   setPagina]   = useState(1);
  const [tam,      setTam]      = useState(20);

  const [clienteId, setClienteId] = useState<number | undefined>();
  const [visao,     setVisao]     = useState<Visao>('sintetico');
  const [drill,     setDrill]     = useState<ClienteDrill | null>(null);
  const { ordenarPor, direcao, cabecalho } = useOrdenacaoServidor<CampoCliente>('liquido', ['codigo', 'nome'], () => setPagina(1));

  const sintetico = visao === 'sintetico';
  const { data, isLoading } = useVendasClientesSintetico({ dtInicio, dtFim, clienteId, pagina, tamanhoPagina: tam, ordenarPor, direcao }, sintetico);
  const analitico = useVendasClientesAnalitico({ dtInicio, dtFim, clienteId, pagina, tamanhoPagina: tam }, !sintetico);
  const exportParams = useMemo(
    () => (sintetico ? { dtInicio, dtFim, clienteId, ordenarPor, direcao } : { dtInicio, dtFim, clienteId }),
    [sintetico, dtInicio, dtFim, clienteId, ordenarPor, direcao],
  );

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="flex flex-wrap gap-3 items-end">
          <SeletorVisao value={visao} onChange={(v) => { setVisao(v); setPagina(1); }} />
          <FiltroPeriodo dtInicio={dtInicio} dtFim={dtFim} onDtInicio={(v) => { setDtInicio(v); setPagina(1); }} onDtFim={(v) => { setDtFim(v); setPagina(1); }} />
          <FiltroPessoa tipo="cliente" value={clienteId} onChange={(v) => { setClienteId(v); setPagina(1); }} />
        </div>
        <BotaoExportar
          endpoint={sintetico ? '/api/relatorios/vendas/clientes/exportar' : '/api/relatorios/vendas/clientes/analitico/exportar'}
          params={exportParams}
        />
      </div>
      <HistoricoClienteDialog cliente={drill} dtInicio={dtInicio} dtFim={dtFim} onClose={() => setDrill(null)} />
      {!sintetico ? (
        <>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="text-right w-[90px]">DAV</TableHead>
                  <TableHead>Emissão</TableHead>
                  <TableHead>Cliente</TableHead>
                  <TableHead className="text-right">Total Bruto</TableHead>
                  <TableHead className="text-right">Desconto</TableHead>
                  <TableHead className="text-right">Total Líquido</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {analitico.isLoading ? (
                  <TableRow><TableCell colSpan={6}><Skeleton className="h-8 w-full" /></TableCell></TableRow>
                ) : !analitico.data?.itens.length ? (
                  <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-8">Nenhum resultado</TableCell></TableRow>
                ) : analitico.data.itens.map((r) => (
                  <TableRow key={r.numeroDav}>
                    <TableCell className="text-right tabular-nums font-medium">{r.numeroDav}</TableCell>
                    <TableCell className="text-muted-foreground text-sm">{fmtData(r.dtEmissao)}</TableCell>
                    <TableCell><NomeClienteLink id={r.clienteId} nome={r.nomeCliente} onAbrir={setDrill} /></TableCell>
                    <TableCell className="text-right tabular-nums">{fmtCur(r.totalBruto)}</TableCell>
                    <TableCell className="text-right tabular-nums text-red-500">{r.totalDesconto > 0 ? `-${fmtCur(r.totalDesconto)}` : '—'}</TableCell>
                    <TableCell className="text-right tabular-nums font-semibold">{fmtCur(r.totalLiquido)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          {analitico.data && <PaginacaoBar pagina={pagina} setPagina={setPagina} tamanhoPagina={tam} setTamanhoPagina={setTam} total={analitico.data.total} totalPaginas={analitico.data.totalPaginas} />}
        </>
      ) : (
      <>
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <SortableTableHead campo="codigo" {...cabecalho} alinharDireita className="w-[90px]">Código</SortableTableHead>
              <SortableTableHead campo="nome" {...cabecalho}>Cliente</SortableTableHead>
              <SortableTableHead campo="qtd" {...cabecalho} alinharDireita>Qtd Docs</SortableTableHead>
              <SortableTableHead campo="bruto" {...cabecalho} alinharDireita>Total Bruto</SortableTableHead>
              <SortableTableHead campo="desconto" {...cabecalho} alinharDireita>Desconto</SortableTableHead>
              <SortableTableHead campo="liquido" {...cabecalho} alinharDireita>Total Líquido</SortableTableHead>
              <SortableTableHead campo="ultimaVenda" {...cabecalho}>Última Venda</SortableTableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow><TableCell colSpan={7}><Skeleton className="h-8 w-full" /></TableCell></TableRow>
            ) : !data?.itens.length ? (
              <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-8">Nenhum resultado</TableCell></TableRow>
            ) : data.itens.map((r) => (
              <TableRow key={`${r.clienteId}-${r.nomeCliente}`}>
                <TableCell className="text-right tabular-nums text-muted-foreground">{r.clienteId > 0 ? r.clienteId : '—'}</TableCell>
                <TableCell className="font-medium"><NomeClienteLink id={r.clienteId} nome={r.nomeCliente} onAbrir={setDrill} /></TableCell>
                <TableCell className="text-right">{r.qtdDocumentos}</TableCell>
                <TableCell className="text-right tabular-nums">{fmtCur(r.totalBruto)}</TableCell>
                <TableCell className="text-right tabular-nums text-red-500">{r.totalDesconto > 0 ? `-${fmtCur(r.totalDesconto)}` : '—'}</TableCell>
                <TableCell className="text-right tabular-nums font-semibold">{fmtCur(r.totalLiquido)}</TableCell>
                <TableCell className="text-muted-foreground text-sm">{new Date(r.ultimaVenda).toLocaleDateString('pt-BR')}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      {data && <PaginacaoBar pagina={pagina} setPagina={setPagina} tamanhoPagina={tam} setTamanhoPagina={setTam} total={data.total} totalPaginas={data.totalPaginas} />}
      </>
      )}
    </div>
  );
}

// ── Aba Vendas por Produto ────────────────────────────────────────────────────

function TabProdutos() {
  const [dtInicio, setDtInicio] = useState(diasAtras(30));
  const [dtFim,    setDtFim]    = useState(hoje());
  const [pagina,   setPagina]   = useState(1);
  const [tam,      setTam]      = useState(20);

  const [produtoId, setProdutoId] = useState<number | undefined>();
  const [grupoId,   setGrupoId]   = useState<number | undefined>();
  const [visao,     setVisao]     = useState<Visao>('sintetico');
  const [drill,     setDrill]     = useState<ClienteDrill | null>(null);
  const { ordenarPor, direcao, cabecalho } = useOrdenacaoServidor<CampoProduto>('total', ['codigo', 'descricao'], () => setPagina(1));

  const sintetico = visao === 'sintetico';
  const { data, isLoading } = useVendasProdutosSintetico({ dtInicio, dtFim, produtoId, grupoId, pagina, tamanhoPagina: tam, ordenarPor, direcao }, sintetico);
  const analitico = useVendasProdutosAnalitico({ dtInicio, dtFim, produtoId, grupoId, pagina, tamanhoPagina: tam }, !sintetico);
  const exportParams = useMemo(
    () => (sintetico
      ? { dtInicio, dtFim, produtoId, grupoId, ordenarPor, direcao }
      : { dtInicio, dtFim, produtoId, grupoId }),
    [sintetico, dtInicio, dtFim, produtoId, grupoId, ordenarPor, direcao],
  );

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="flex flex-wrap gap-3 items-end">
          <SeletorVisao value={visao} onChange={(v) => { setVisao(v); setPagina(1); }} />
          <FiltroPeriodo dtInicio={dtInicio} dtFim={dtFim} onDtInicio={(v) => { setDtInicio(v); setPagina(1); }} onDtFim={(v) => { setDtFim(v); setPagina(1); }} />
          <FiltroProduto value={produtoId} onChange={(v) => { setProdutoId(v); setPagina(1); }} />
          <FiltroGrupoProduto value={grupoId} onChange={(v) => { setGrupoId(v); setPagina(1); }} />
        </div>
        <BotaoExportar
          endpoint={sintetico ? '/api/relatorios/vendas/produtos/exportar' : '/api/relatorios/vendas/produtos/analitico/exportar'}
          params={exportParams}
        />
      </div>
      <HistoricoClienteDialog cliente={drill} dtInicio={dtInicio} dtFim={dtFim} onClose={() => setDrill(null)} />
      {!sintetico ? (
        <>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="text-right w-[90px]">DAV</TableHead>
                  <TableHead>Emissão</TableHead>
                  <TableHead>Cliente</TableHead>
                  <TableHead className="text-right w-[90px]">Código</TableHead>
                  <TableHead>Produto</TableHead>
                  <TableHead className="text-right">Qtd</TableHead>
                  <TableHead className="text-right">Vlr Unit.</TableHead>
                  <TableHead className="text-right">Desconto</TableHead>
                  <TableHead className="text-right">Valor Total</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {analitico.isLoading ? (
                  <TableRow><TableCell colSpan={9}><Skeleton className="h-8 w-full" /></TableCell></TableRow>
                ) : !analitico.data?.itens.length ? (
                  <TableRow><TableCell colSpan={9} className="text-center text-muted-foreground py-8">Nenhum resultado</TableCell></TableRow>
                ) : analitico.data.itens.map((r, i) => (
                  <TableRow key={`${r.numeroDav}-${r.produtoId}-${i}`}>
                    <TableCell className="text-right tabular-nums font-medium">{r.numeroDav}</TableCell>
                    <TableCell className="text-muted-foreground text-sm">{fmtData(r.dtEmissao)}</TableCell>
                    <TableCell><NomeClienteLink id={r.clienteId} nome={r.nomeCliente} onAbrir={setDrill} /></TableCell>
                    <TableCell className="text-right tabular-nums text-muted-foreground">{r.produtoId}</TableCell>
                    <TableCell>{r.descricaoProduto}</TableCell>
                    <TableCell className="text-right tabular-nums">{fmtNum(r.quantidade)}</TableCell>
                    <TableCell className="text-right tabular-nums">{fmtCur(r.valorUnitario)}</TableCell>
                    <TableCell className="text-right tabular-nums text-red-500">{r.valorDesconto > 0 ? `-${fmtCur(r.valorDesconto)}` : '—'}</TableCell>
                    <TableCell className="text-right tabular-nums font-semibold">{fmtCur(r.valorTotal)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          {analitico.data && <PaginacaoBar pagina={pagina} setPagina={setPagina} tamanhoPagina={tam} setTamanhoPagina={setTam} total={analitico.data.total} totalPaginas={analitico.data.totalPaginas} />}
        </>
      ) : (
      <>
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <SortableTableHead campo="codigo" {...cabecalho} alinharDireita className="w-[90px]">Código</SortableTableHead>
              <SortableTableHead campo="descricao" {...cabecalho}>Produto</SortableTableHead>
              <SortableTableHead campo="qtd" {...cabecalho} alinharDireita>Qtd Vendida</SortableTableHead>
              <SortableTableHead campo="total" {...cabecalho} alinharDireita>Valor Total</SortableTableHead>
              <SortableTableHead campo="desconto" {...cabecalho} alinharDireita>Desconto</SortableTableHead>
              <SortableTableHead campo="liquido" {...cabecalho} alinharDireita>Valor Líquido</SortableTableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow><TableCell colSpan={6}><Skeleton className="h-8 w-full" /></TableCell></TableRow>
            ) : !data?.itens.length ? (
              <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-8">Nenhum resultado</TableCell></TableRow>
            ) : data.itens.map((r) => (
              <TableRow key={r.produtoId}>
                <TableCell className="text-right tabular-nums text-muted-foreground">{r.produtoId}</TableCell>
                <TableCell className="font-medium">{r.descricao}</TableCell>
                <TableCell className="text-right tabular-nums">{fmtNum(r.qtdTotal)}</TableCell>
                <TableCell className="text-right tabular-nums">{fmtCur(r.valorTotal)}</TableCell>
                <TableCell className="text-right tabular-nums text-red-500">{r.valorDesconto > 0 ? `-${fmtCur(r.valorDesconto)}` : '—'}</TableCell>
                <TableCell className="text-right tabular-nums font-semibold">{fmtCur(r.valorLiquido)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      {data && <PaginacaoBar pagina={pagina} setPagina={setPagina} tamanhoPagina={tam} setTamanhoPagina={setTam} total={data.total} totalPaginas={data.totalPaginas} />}
      </>
      )}
    </div>
  );
}

// ── Aba Formas de Pagamento ───────────────────────────────────────────────────

function TabFormasPagamento() {
  const [dtInicio, setDtInicio] = useState(diasAtras(30));
  const [dtFim,    setDtFim]    = useState(hoje());

  const { data, isLoading } = useVendasFormasPagamento({ dtInicio, dtFim });
  const total = data?.reduce((s, x) => s + x.totalVendas, 0) ?? 0;
  const exportParams = useMemo(() => ({ dtInicio, dtFim }), [dtInicio, dtFim]);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="flex flex-wrap gap-3 items-end"><FiltroPeriodo dtInicio={dtInicio} dtFim={dtFim} onDtInicio={setDtInicio} onDtFim={setDtFim} /></div>
        <BotaoExportar endpoint="/api/relatorios/vendas/formas-pagamento/exportar" params={exportParams} />
      </div>
      {isLoading ? (
        <div className="space-y-3">{Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-12 w-full" />)}</div>
      ) : !data?.length ? (
        <p className="text-muted-foreground text-center py-8">Nenhum dado no período.</p>
      ) : (
        <div className="space-y-3">
          <div className="flex justify-between text-sm text-muted-foreground pb-1 border-b">
            <span>Total do período: <span className="font-semibold text-foreground">{fmtCur(total)}</span></span>
            <span>{data.length} forma{data.length !== 1 ? 's' : ''}</span>
          </div>
          {data.map((r) => (
            <div key={r.formaPagRecId} className="space-y-1">
              <div className="flex justify-between text-sm">
                <span className="font-medium">{r.descricaoFormaPag}</span>
                <div className="flex gap-4 tabular-nums">
                  <span className="text-muted-foreground">{r.qtdTransacoes} transações</span>
                  <span>{fmtNum(r.percentual)}%</span>
                  <span className="font-semibold">{fmtCur(r.totalVendas)}</span>
                </div>
              </div>
              <Progress value={r.percentual} className="h-2" />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Aba Evolução de Vendas ────────────────────────────────────────────────────

function TabEvolucao() {
  const [dtInicio, setDtInicio] = useState(anoInicio());
  const [dtFim,    setDtFim]    = useState(hoje());

  const { data, isLoading } = useEvolucaoVendas({ dtInicio, dtFim });
  const maxLiquido = Math.max(...(data?.map((x) => x.totalLiquido) ?? [1]));
  const exportParams = useMemo(() => ({ dtInicio, dtFim }), [dtInicio, dtFim]);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="flex flex-wrap gap-3 items-end"><FiltroPeriodo dtInicio={dtInicio} dtFim={dtFim} onDtInicio={setDtInicio} onDtFim={setDtFim} /></div>
        <BotaoExportar endpoint="/api/relatorios/vendas/evolucao/exportar" params={exportParams} />
      </div>
      {isLoading ? (
        <div className="space-y-3">{Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-10 w-full" />)}</div>
      ) : !data?.length ? (
        <p className="text-muted-foreground text-center py-8">Nenhum dado no período.</p>
      ) : (
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Mês/Ano</TableHead>
                <TableHead className="text-right">Qtd Docs</TableHead>
                <TableHead className="text-right">Total Bruto</TableHead>
                <TableHead className="text-right">Total Líquido</TableHead>
                <TableHead className="text-right">Ticket Médio</TableHead>
                <TableHead className="w-40">Proporção</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.map((r) => (
                <TableRow key={`${r.ano}-${r.mes}`}>
                  <TableCell className="font-medium">{r.mesDescricao}/{r.ano}</TableCell>
                  <TableCell className="text-right">{r.qtdDocumentos}</TableCell>
                  <TableCell className="text-right tabular-nums">{fmtCur(r.totalBruto)}</TableCell>
                  <TableCell className="text-right tabular-nums font-semibold">{fmtCur(r.totalLiquido)}</TableCell>
                  <TableCell className="text-right tabular-nums">{fmtCur(r.ticketMedio)}</TableCell>
                  <TableCell>
                    <Progress value={maxLiquido > 0 ? (r.totalLiquido / maxLiquido) * 100 : 0} className="h-2" />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}

// ── Página ────────────────────────────────────────────────────────────────────

const RelatoriosVendas = () => (
  <div className="p-6 space-y-6">
    <div>
      <h1 className="text-3xl font-bold text-foreground">Relatórios de Vendas</h1>
      <p className="text-muted-foreground mt-1">Análise de vendas por cliente, produto, forma de pagamento e evolução.</p>
    </div>
    <Tabs defaultValue="clientes">
      <TabsList className="flex-wrap">
        <TabsTrigger value="clientes"   className="gap-2"><Users      className="h-4 w-4" />Por Cliente</TabsTrigger>
        <TabsTrigger value="produtos"   className="gap-2"><Package    className="h-4 w-4" />Por Produto</TabsTrigger>
        <TabsTrigger value="pagamento"  className="gap-2"><CreditCard className="h-4 w-4" />Forma de Pagamento</TabsTrigger>
        <TabsTrigger value="evolucao"   className="gap-2"><TrendingUp className="h-4 w-4" />Evolução Mensal</TabsTrigger>
      </TabsList>
      <TabsContent value="clientes">  <Card><CardHeader><CardTitle>Vendas por Cliente</CardTitle></CardHeader><CardContent><TabClientes /></CardContent></Card></TabsContent>
      <TabsContent value="produtos">  <Card><CardHeader><CardTitle>Vendas por Produto</CardTitle></CardHeader><CardContent><TabProdutos /></CardContent></Card></TabsContent>
      <TabsContent value="pagamento"> <Card><CardHeader><CardTitle>Vendas por Forma de Pagamento</CardTitle></CardHeader><CardContent><TabFormasPagamento /></CardContent></Card></TabsContent>
      <TabsContent value="evolucao">  <Card><CardHeader><CardTitle>Evolução Mensal de Vendas</CardTitle></CardHeader><CardContent><TabEvolucao /></CardContent></Card></TabsContent>
    </Tabs>
  </div>
);

export default RelatoriosVendas;
